import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Activity, AlertTriangle, CheckCircle2, Copy, Loader2, Link2, Send, Radio,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Configurações > Monitoramento.
 *
 * Watchdogs de sistema — os alertas que não pertencem a um equipamento (alertas
 * de equipamento ficam em Equipamentos > equipamento > Alertas, Fase 1000).
 * Antes de 2026-09-09 a configuração destes vivia só no banco, sem tela: quem
 * recebia o aviso de queda do WhatsApp só mudava editando o Mongo à mão.
 */
const SystemMonitorsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(null);
  const [monitors, setMonitors] = useState([]);
  const [channels, setChannels] = useState({ telegram: {} });
  const [botToken, setBotToken] = useState('');
  const [discovered, setDiscovered] = useState([]);
  const [pairingUrl, setPairingUrl] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/system-monitors`);
      setMonitors(data.monitors || []);
      setChannels(data.channels || { telegram: {} });
    } catch (e) {
      toast.error('Não foi possível carregar os monitores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchLocal = (key, patch) => {
    setMonitors((prev) => prev.map((m) => (m.key === key ? { ...m, ...patch } : m)));
  };

  const save = async (monitor) => {
    setSaving(monitor.key);
    try {
      await axios.put(`${API}/system-monitors/${monitor.key}`, {
        enabled: monitor.enabled,
        period_minutes: monitor.period_minutes,
        threshold: monitor.threshold,
        recipients: monitor.recipients,
      });
      toast.success('Monitoramento salvo');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao salvar');
    } finally {
      setSaving(null);
    }
  };

  const testChannels = async (monitor) => {
    setTesting(monitor.key);
    try {
      const { data } = await axios.post(`${API}/system-monitors/${monitor.key}/test`);
      const partes = [];
      if (data.email?.attempted) {
        partes.push(data.email.delivered ? 'e-mail entregue' : 'e-mail FALHOU');
      }
      if (data.telegram?.attempted) {
        partes.push(data.telegram.delivered ? 'Telegram entregue' : 'Telegram FALHOU');
      }
      if (!partes.length) {
        toast.warning('Nenhum destinatário configurado para testar');
      } else {
        const falhou = partes.some((p) => p.includes('FALHOU'));
        (falhou ? toast.error : toast.success)(partes.join(' · '));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha no teste');
    } finally {
      setTesting(null);
    }
  };

  const saveTelegram = async () => {
    setSaving('telegram');
    try {
      const { data } = await axios.put(`${API}/system-monitors/channels/telegram`, {
        enabled: true,
        bot_token: botToken || undefined,
      });
      setChannels(data);
      setBotToken('');
      toast.success('Bot do Telegram salvo');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao salvar o bot');
    } finally {
      setSaving(null);
    }
  };

  const discoverChats = async () => {
    setSaving('discover');
    try {
      const { data } = await axios.post(`${API}/system-monitors/channels/telegram/discover`);
      setDiscovered(data.chats || []);
      if (!data.chats?.length) {
        toast.warning('Ninguém falou com o bot ainda. Mande uma mensagem para ele e tente de novo.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao consultar o Telegram');
    } finally {
      setSaving(null);
    }
  };

  const generateLink = async (monitor) => {
    setSaving(`${monitor.key}-link`);
    try {
      const { data } = await axios.post(`${API}/system-monitors/${monitor.key}/pairing-link`);
      setPairingUrl(data.url);
      toast.success(`Link gerado (validade ${data.expires_in_hours}h)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao gerar o link');
    } finally {
      setSaving(null);
    }
  };

  const revokeLink = async (monitor) => {
    setSaving(`${monitor.key}-link`);
    try {
      await axios.delete(`${API}/system-monitors/${monitor.key}/pairing-link`);
      setPairingUrl(null);
      toast.success('Link revogado');
    } catch (e) {
      toast.error('Falha ao revogar');
    } finally {
      setSaving(null);
    }
  };

  const setEmails = (monitor, text) => {
    const emails = text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    patchLocal(monitor.key, { recipients: { ...monitor.recipients, emails } });
  };

  const toggleChat = (monitor, chatId) => {
    const atuais = monitor.recipients?.telegram_chat_ids || [];
    const proximos = atuais.includes(chatId)
      ? atuais.filter((c) => c !== chatId)
      : [...atuais, chatId];
    patchLocal(monitor.key, { recipients: { ...monitor.recipients, telegram_chat_ids: proximos } });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-10 justify-center text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando monitores…
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {monitors.map((m) => {
        const live = m.live || {};
        const emFalha = live.ok === false;
        return (
          <Card key={m.key}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <Activity className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {m.label}
                      {emFalha ? (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="w-3 h-3" /> Fora do ar
                        </Badge>
                      ) : (
                        <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600">
                          <CheckCircle2 className="w-3 h-3" /> Operando
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">{m.description}</CardDescription>
                  </div>
                </div>
                <Switch
                  checked={m.enabled}
                  onCheckedChange={(v) => patchLocal(m.key, { enabled: v })}
                  aria-label="Monitoramento ativo"
                />
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {live.detail && (
                <div className="text-sm p-3 rounded-lg bg-muted/50 text-muted-foreground">
                  {live.detail}
                </div>
              )}
              {m.consecutive_failures > 0 && (
                <div className="text-sm text-amber-600">
                  {m.consecutive_failures} verificação(ões) consecutiva(s) em falha
                  {m.notified ? ' — destinatários já avisados' : ''}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Verificar a cada (minutos)</Label>
                  <Input
                    type="number" min={1}
                    value={m.period_minutes}
                    onChange={(e) => patchLocal(m.key, { period_minutes: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Avisar após quantas falhas seguidas</Label>
                  <Input
                    type="number" min={1}
                    value={m.threshold}
                    onChange={(e) => patchLocal(m.key, { threshold: Number(e.target.value) })}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Quem recebe o aviso (um e-mail por linha)</Label>
                <textarea
                  className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={(m.recipients?.emails || []).join('\n')}
                  onChange={(e) => setEmails(m, e.target.value)}
                  placeholder="fulano@empresa.com"
                />
                <p className="text-xs text-muted-foreground">
                  Sem destinatário, o alerta ainda aparece na Central — mas ninguém é avisado.
                </p>
              </div>

              {discovered.length > 0 && (
                <div className="space-y-2">
                  <Label>Telegram — quem recebe</Label>
                  <div className="flex flex-wrap gap-2">
                    {discovered.map((c) => {
                      const on = (m.recipients?.telegram_chat_ids || []).includes(c.chat_id);
                      return (
                        <Button
                          key={c.chat_id}
                          type="button"
                          size="sm"
                          variant={on ? 'default' : 'outline'}
                          onClick={() => toggleChat(m, c.chat_id)}
                        >
                          <Radio className="w-3 h-3 mr-1" />
                          {c.name || c.username || c.chat_id}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={() => save(m)} disabled={saving === m.key}>
                  {saving === m.key && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Salvar
                </Button>
                <Button variant="outline" onClick={() => testChannels(m)} disabled={testing === m.key}>
                  {testing === m.key
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <Send className="w-4 h-4 mr-2" />}
                  Testar canais
                </Button>
                {m.action === 'pairing_link' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => generateLink(m)}
                      disabled={saving === `${m.key}-link`}
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Gerar link de repareamento
                    </Button>
                    {pairingUrl && (
                      <Button variant="ghost" onClick={() => revokeLink(m)}>Revogar link</Button>
                    )}
                  </>
                )}
              </div>

              {pairingUrl && m.action === 'pairing_link' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                  <code className="text-xs break-all flex-1">{pairingUrl}</code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard?.writeText(pairingUrl);
                      toast.success('Link copiado');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-sky-500" />
            Canal Telegram
          </CardTitle>
          <CardDescription>
            Canal de aviso independente do e-mail e do próprio WhatsApp. Crie um bot no
            @BotFather, cole o token abaixo e mande uma mensagem para o bot — depois use
            “Procurar conversas” para escolher quem recebe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <span className="text-sm">
              {channels.telegram?.configured
                ? `Bot configurado (${channels.telegram.bot_token_masked})`
                : 'Nenhum bot configurado'}
            </span>
            <Badge variant={channels.telegram?.enabled ? 'default' : 'secondary'}>
              {channels.telegram?.enabled ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tg-token">Token do bot</Label>
            <Input
              id="tg-token"
              type="password"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder={channels.telegram?.configured ? 'deixe em branco para manter' : '123456:ABC-DEF…'}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={saveTelegram} disabled={saving === 'telegram'}>
              {saving === 'telegram' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar bot
            </Button>
            <Button variant="outline" onClick={discoverChats} disabled={saving === 'discover'}>
              {saving === 'discover' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Procurar conversas
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemMonitorsCard;
